import React, { useState } from "react";
import { Mic, Square, Volume2, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { getMicStream, micErrorMessage } from "@/lib/mic";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

// phrase: { phrase_id?, arabic, translit, english }
export function PracticeDialog({ phrase, open, onOpenChange, onMastered }) {
  const { t, lang } = useI18n();
  const [recording, setRecording] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  React.useEffect(() => {
    if (!open) {
      setResult(null);
      setRecording(false);
      setScoring(false);
    }
  }, [open]);

  if (!phrase) return null;

  const listen = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const { data } = await api.post("/tts", { text: phrase.arabic, lang: "ar" });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch {
      setPlaying(false);
      toast.error(t("tryAgain"));
    }
  };

  const score = async (blob) => {
    setScoring(true);
    const form = new FormData();
    form.append("file", blob, "practice.webm");
    if (phrase.phrase_id) form.append("phrase_id", phrase.phrase_id);
    form.append("target_text", phrase.arabic || "");
    try {
      const { data } = await api.post("/learning/pronounce", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.mastered && onMastered) onMastered(phrase.phrase_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("tryAgain"));
    } finally {
      setScoring(false);
    }
  };

  const startRecording = async () => {
    setResult(null);
    try {
      const stream = await getMicStream();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((tk) => tk.stop());
        score(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(micErrorMessage(err, lang));
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="practice-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{t("practiceTitle")}</DialogTitle>
          <DialogDescription>{t("practiceIntro")}</DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border border-border p-4 mt-2 text-center">
          <div className="text-2xl font-semibold text-foreground" dir="rtl" data-testid="practice-arabic">{phrase.arabic}</div>
          {phrase.translit && <div className="text-sm text-accent mt-1 italic">{phrase.translit}</div>}
          {phrase.english && <div className="text-sm text-muted-foreground mt-1">{phrase.english}</div>}
          <button
            onClick={listen}
            disabled={playing}
            data-testid="practice-listen-btn"
            className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full border border-border text-sm font-medium hover:bg-secondary disabled:opacity-60"
          >
            {playing ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />} {t("listenLabel")}
          </button>
        </div>

        <div className="mt-2 grid place-items-center">
          {scoring ? (
            <div className="flex items-center gap-2 text-primary py-3"><Loader2 className="animate-spin" size={18} /> …</div>
          ) : recording ? (
            <button
              onClick={stopRecording}
              data-testid="practice-stop-btn"
              className="flex items-center gap-2 h-12 px-6 rounded-full bg-destructive text-white font-semibold animate-pulse"
            >
              <Square size={18} className="fill-current" /> {lang === "ar" ? "إيقاف" : "Stop"}
            </button>
          ) : (
            <button
              onClick={startRecording}
              data-testid="practice-record-btn"
              className="flex items-center gap-2 h-12 px-6 rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
            >
              <Mic size={18} /> {result ? <><RotateCcw size={16} /> {t("tryAgain")}</> : t("recordPronunciation")}
            </button>
          )}
        </div>

        {result && (
          <div className="rounded-2xl bg-secondary p-4 mt-1" data-testid="practice-result">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">{t("yourScore")}</span>
              <span className={`font-head font-bold text-2xl ${result.correct ? "text-accent" : "text-foreground"}`} data-testid="practice-score">{result.score}%</span>
            </div>
            <Progress value={result.score} className="h-2" />
            <div className="text-sm mt-3 flex items-start gap-2">
              {result.correct ? <CheckCircle2 size={16} className="text-accent mt-0.5 shrink-0" /> : null}
              <span className={result.correct ? "text-accent font-medium" : "text-muted-foreground"}>
                {result.correct ? t("greatJob") : t("keepPracticing")}
              </span>
            </div>
            {result.heard && (
              <div className="text-xs text-muted-foreground mt-2" dir="rtl">
                {t("weHeard")}: <span className="text-foreground">{result.heard}</span>
              </div>
            )}
            {result.mastered && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-accent bg-accent/10 rounded-full px-2.5 py-1">
                <CheckCircle2 size={13} /> {t("masteredBadge")}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
